import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export type EnvelopeStatus =
  | 'draft'
  | 'sent'
  | 'delivered'
  | 'completed'
  | 'declined'
  | 'voided'
  | 'expired';

export interface EnvelopeSigner {
  id: string;
  name: string;
  email: string;
  order: number;
  status: 'pending' | 'sent' | 'delivered' | 'signed' | 'declined';
  signedAt: string | null;
}

export interface Envelope {
  id: string;
  tenantId: string;
  documentId: string;
  documentName: string;
  clientId: string | null;
  clientName: string | null;
  jobId: string | null;
  jobTitle: string | null;
  status: EnvelopeStatus;
  signers: EnvelopeSigner[];
  dueDate: string | null;
  sentAt: string | null;
  completedAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  signedDocumentUrl: string | null;
  createdById: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface EnvelopeEvent {
  id: string;
  envelopeId: string;
  eventType: string;
  description: string;
  occurredAt: string;
}

export interface ESignatureConfig {
  id: string;
  tenantId: string;
  provider: 'docusign';
  accountId: string;
  isConnected: boolean;
  connectedAt: string | null;
  lastSyncAt: string | null;
}

// ─── Request Types ───────────────────────────────────────────────────────────

export interface CreateEnvelopeRequest {
  documentId: string;
  signers: { name: string; email: string; order: number }[];
  dueDate?: string;
  clientId?: string;
  jobId?: string;
  message?: string;
}

export interface EnvelopesListParams {
  status?: EnvelopeStatus | '';
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface UpdateConfigRequest {
  accountId?: string;
  integrationKey?: string;
  secretKey?: string;
  rsaPrivateKey?: string;
}

/** Paginated response */
interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Query Keys ──────────────────────────────────────────────────────────────

const esignatureKeys = {
  all: ['e-signatures'] as const,
  envelopes: () => [...esignatureKeys.all, 'envelopes'] as const,
  envelopeList: (params: EnvelopesListParams) => [...esignatureKeys.envelopes(), params] as const,
  envelope: (id: string) => [...esignatureKeys.envelopes(), id] as const,
  envelopeEvents: (id: string) => [...esignatureKeys.envelope(id), 'events'] as const,
  config: () => [...esignatureKeys.all, 'config'] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Hook to fetch paginated list of e-signature envelopes.
 * Validates: Requirements 20.1, 20.2
 */
export function useEnvelopesList(params: EnvelopesListParams = {}) {
  return useQuery({
    queryKey: esignatureKeys.envelopeList(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.set('status', params.status);
      if (params.page) queryParams.set('page', String(params.page));
      if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));
      if (params.search) queryParams.set('search', params.search);

      const response = await apiClient.get<ApiResponse<PaginatedResponse<Envelope>>>(
        `/e-signatures/envelopes?${queryParams.toString()}`,
      );
      return response.data.data;
    },
  });
}

/**
 * Hook to fetch a single envelope by ID with full details.
 */
export function useEnvelope(id: string | null) {
  return useQuery({
    queryKey: esignatureKeys.envelope(id ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Envelope>>(`/e-signatures/envelopes/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

/**
 * Hook to fetch envelope events/timeline.
 */
export function useEnvelopeEvents(id: string | null) {
  return useQuery({
    queryKey: esignatureKeys.envelopeEvents(id ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<EnvelopeEvent[]>>(
        `/e-signatures/envelopes/${id}/events`,
      );
      return response.data.data;
    },
    enabled: !!id,
  });
}

/**
 * Hook to create a new e-signature envelope (send for signing).
 */
export function useCreateEnvelope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateEnvelopeRequest) => {
      const response = await apiClient.post<ApiResponse<Envelope>>('/e-signatures/envelopes', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: esignatureKeys.envelopes() });
    },
  });
}

/**
 * Hook to void an envelope.
 */
export function useVoidEnvelope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await apiClient.post<ApiResponse<Envelope>>(
        `/e-signatures/envelopes/${id}/void`,
        { reason },
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: esignatureKeys.envelope(variables.id) });
      queryClient.invalidateQueries({ queryKey: esignatureKeys.envelopes() });
    },
  });
}

/**
 * Hook to send a reminder for an envelope.
 */
export function useRemindEnvelope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<ApiResponse<{ sent: boolean }>>(
        `/e-signatures/envelopes/${id}/remind`,
      );
      return response.data.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: esignatureKeys.envelope(id) });
    },
  });
}

/**
 * Hook to fetch DocuSign configuration.
 */
export function useESignatureConfig() {
  return useQuery({
    queryKey: esignatureKeys.config(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<ESignatureConfig>>('/e-signatures/config');
      return response.data.data;
    },
  });
}

/**
 * Hook to update DocuSign configuration.
 */
export function useUpdateESignatureConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateConfigRequest) => {
      const response = await apiClient.put<ApiResponse<ESignatureConfig>>(
        '/e-signatures/config',
        data,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: esignatureKeys.config() });
    },
  });
}
